import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, X, Star, Lock, Plus, Check, Loader2, Server } from "lucide-react";
import { EFFECTS, CATEGORIES, isEffectLocked } from "@/lib/effects";
import { createEngine } from "@/lib/glEngine";

const TABS = [
    { id: "all", label: "All" },
    ...CATEGORIES,
    { id: "free", label: "Free" },
    { id: "premium", label: "Premium" },
    { id: "favorites", label: "Favorites" },
    { id: "recent", label: "Recent" },
];

export default function EffectsLibrary({
    open,
    onClose,
    baseFrame,
    baseToken,
    activeId,
    favorites,
    recents,
    onApply,
    onToggleFav,
}) {
    const [query, setQuery] = useState("");
    const [tab, setTab] = useState("all");
    const [thumbs, setThumbs] = useState({});
    const thumbCanvasRef = useRef(null);

    const list = useMemo(() => {
        const q = query.trim().toLowerCase();
        return EFFECTS.filter((e) => {
            if (q && !e.name.toLowerCase().includes(q)) return false;
            if (tab === "all") return true;
            if (tab === "free") return !e.premium;
            if (tab === "premium") return !!e.premium;
            if (tab === "favorites") return favorites.includes(e.id);
            if (tab === "recent") return recents.includes(e.id);
            return e.cat === tab;
        });
    }, [query, tab, favorites, recents]);

    // reset generated thumbnails whenever the base frame changes
    useEffect(() => {
        setThumbs({});
    }, [baseToken]);

    // generate real shader previews lazily, one per tick, so phones never block
    useEffect(() => {
        if (!open || !baseFrame) return;
        let cancelled = false;
        if (!thumbCanvasRef.current) thumbCanvasRef.current = document.createElement("canvas");
        const tc = thumbCanvasRef.current;
        tc.width = 160;
        tc.height = 200;
        const engine = createEngine(tc);
        const queue = list.map((e) => e.id);
        const cache = {};
        const step = () => {
            if (cancelled) return;
            const id = queue.shift();
            if (!id) return;
            const def = EFFECTS.find((x) => x.id === id);
            if (!cache[id]) {
                let url = null;
                if (def && def.body && engine) {
                    try {
                        const ok = engine.render(def.body, baseFrame, {
                            time: 0.35,
                            prog: 0.6,
                            intensity: 1,
                            speed: 1,
                            amount: def.amountDefault ?? 0.5,
                            opacity: 1,
                        });
                        if (ok) url = tc.toDataURL("image/jpeg", 0.72);
                    } catch (err) {
                        url = null;
                    }
                }
                cache[id] = url;
                setThumbs((t) => ({ ...t, [id]: url }));
            }
            setTimeout(step, 16);
        };
        step();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, baseToken, tab, query]);

    if (!open) return null;

    const handleCardClick = (def) => {
        if (isEffectLocked(def)) {
            toast.info(
                def.server
                    ? "This effect needs server-side processing — coming soon to Deoeditz."
                    : "Premium effect — it unlocks as soon as the premium system is enabled."
            );
            return;
        }
        onApply(def);
    };

    return (
        <div className="fixed inset-0 z-[60] flex flex-col bg-obsidian/97 backdrop-blur-sm">
            <div className="border-b border-white/10 bg-obsidian/95">
                <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
                    <div>
                        <h2 className="font-display text-xl font-black tracking-tight text-slate-50">
                            Effects &amp; Animations
                        </h2>
                        <p className="font-mono text-[10px] tracking-[0.18em] text-slate-500">
                            {EFFECTS.length} EFFECTS · TAP TO APPLY
                        </p>
                    </div>
                    <button
                        data-testid="effects-close-button"
                        onClick={onClose}
                        aria-label="Close effects library"
                        className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-slate-200 transition-colors hover:border-amberx hover:text-amberx"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="mx-auto max-w-5xl px-4 pb-3">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                        <input
                            data-testid="effects-search-input"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search effects…"
                            className="h-12 w-full rounded-2xl border border-white/15 bg-surface pl-11 pr-4 text-base text-slate-100 placeholder:text-slate-500 focus:border-amberx focus:outline-none"
                        />
                    </div>
                </div>
                <div className="no-scrollbar mx-auto flex max-w-5xl gap-2 overflow-x-auto px-4 pb-3">
                    {TABS.map((t) => {
                        const active = tab === t.id;
                        let count = null;
                        if (t.id === "favorites") count = favorites.length;
                        if (t.id === "recent") count = recents.length;
                        return (
                            <button
                                key={t.id}
                                data-testid={`effects-tab-${t.id}`}
                                onClick={() => setTab(t.id)}
                                className={`shrink-0 rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
                                    active
                                        ? "border-amberx bg-amberx text-obsidian"
                                        : "border-white/15 text-slate-300 hover:border-white/40"
                                }`}
                            >
                                {t.label}
                                {count > 0 && <span className={active ? "ml-1.5" : "ml-1.5 text-amberx"}>{count}</span>}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
                <div className="mx-auto grid max-w-5xl grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    {list.map((def) => {
                        const locked = isEffectLocked(def);
                        const isActive = activeId === def.id;
                        const fav = favorites.includes(def.id);
                        const thumb = thumbs[def.id];
                        const generating = thumb === undefined;
                        return (
                            <div
                                key={def.id}
                                data-testid={`effect-card-${def.id}`}
                                onClick={() => handleCardClick(def)}
                                className={`group relative cursor-pointer overflow-hidden rounded-2xl border bg-surface transition-all ${
                                    isActive
                                        ? "border-amberx shadow-[0_0_18px_rgba(255,159,28,0.3)]"
                                        : "border-white/10 hover:border-white/35"
                                } ${locked ? "opacity-80" : ""}`}
                            >
                                <div className="relative aspect-[3/4] w-full bg-obsidian">
                                    {thumb ? (
                                        <img src={thumb} alt={`${def.name} preview`} className="h-full w-full object-cover" />
                                    ) : def.server ? (
                                        <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-slate-500">
                                            <Server className="h-7 w-7" />
                                            <span className="px-2 text-center font-mono text-[9px] tracking-wider">
                                                SERVER PROCESSING SOON
                                            </span>
                                        </div>
                                    ) : generating ? (
                                        <div className="flex h-full w-full items-center justify-center">
                                            <Loader2 className="h-5 w-5 animate-spin text-slate-600" />
                                        </div>
                                    ) : (
                                        <div className="flex h-full w-full items-center justify-center text-slate-700">
                                            <Lock className="h-6 w-6" />
                                        </div>
                                    )}
                                    {locked && thumb && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-obsidian/55">
                                            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-obsidian/80 text-amberx">
                                                <Lock className="h-4 w-4" />
                                            </span>
                                        </div>
                                    )}
                                    <button
                                        data-testid={`effect-fav-${def.id}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onToggleFav(def.id);
                                        }}
                                        aria-label={fav ? "Remove from favorites" : "Add to favorites"}
                                        className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full bg-obsidian/70 backdrop-blur-sm transition-colors hover:bg-obsidian"
                                    >
                                        <Star className={`h-4 w-4 ${fav ? "fill-amberx text-amberx" : "text-slate-300"}`} />
                                    </button>
                                    <span
                                        className={`absolute left-2 top-2 rounded-full px-2 py-0.5 font-mono text-[8px] font-bold tracking-[0.14em] ${
                                            def.premium ? "bg-amberx text-obsidian" : "bg-obsidian/70 text-slate-300"
                                        }`}
                                    >
                                        {def.premium ? "PREMIUM" : "FREE"}
                                    </span>
                                    <span
                                        className={`absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full ${
                                            isActive
                                                ? "bg-amberx text-obsidian"
                                                : locked
                                                    ? "bg-obsidian/80 text-slate-300"
                                                    : "bg-amberx/90 text-obsidian"
                                        }`}
                                    >
                                        {isActive ? (
                                            <Check className="h-4 w-4" />
                                        ) : locked ? (
                                            <Lock className="h-3.5 w-3.5" />
                                        ) : (
                                            <Plus className="h-4 w-4" />
                                        )}
                                    </span>
                                </div>
                                <div className="px-3 py-2">
                                    <p className="truncate font-display text-xs font-bold text-slate-100">{def.name}</p>
                                    <p className="font-mono text-[8px] uppercase tracking-[0.16em] text-slate-500">
                                        {def.cat}
                                        {isActive ? " · ACTIVE" : ""}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {list.length === 0 && (
                    <p className="py-16 text-center text-sm text-slate-500">
                        {tab === "favorites"
                            ? "No favorites yet — tap the star on any effect."
                            : tab === "recent"
                                ? "Effects you use will show up here."
                                : "No effects match your search."}
                    </p>
                )}
            </div>
        </div>
    );
}
